import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { ClickableCard, ActiveFilterChip } from '../components/ClickableCard';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, AlertOctagon, Download, Upload, FileSpreadsheet, X } from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { fetchTenantBranding } from '../utils/tenantBranding';

const TONES = { slate: 'text-slate-900', red: 'text-red-600', orange: 'text-orange-600', amber: 'text-amber-600', green: 'text-emerald-600', blue: 'text-blue-600', purple: 'text-purple-600', gray: 'text-slate-500' };
const SEVERITY_CARDS = [
  { key: 'critical', label: 'Critical', tone: 'red' },
  { key: 'high', label: 'High', tone: 'orange' },
  { key: 'medium', label: 'Medium', tone: 'amber' },
  { key: 'low', label: 'Low', tone: 'green' },
];
const STATUS_CARDS = [
  { key: 'reported', label: 'Reported', tone: 'red' },
  { key: 'investigating', label: 'Under Investigation', tone: 'amber' },
  { key: 'contained', label: 'Contained', tone: 'blue' },
  { key: 'resolved', label: 'Resolved', tone: 'green' },
  { key: 'closed', label: 'Closed', tone: 'gray' },
];

function IncidentStatCard({ label, value, testid, tone = 'slate', active, onClick }) {
  return (
    <ClickableCard active={active} onClick={onClick} testid={`${testid}-card`}>
      <div className="bg-white border border-slate-200 rounded-lg py-3 px-4">
        <div className={`text-2xl font-bold ${TONES[tone]}`} data-testid={testid}>{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      </div>
    </ClickableCard>
  );
}

export default function IncidentRegisterPage() {
  const { viewingOrg } = useContext(AuthContext);
  const vtq = () => (viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '');

  const [incidents, setIncidents] = useState([]);
  const [searchParams] = useSearchParams();
  const [cardFilter, setCardFilter] = useState(() => {
    const field = ['severity', 'status'].find((f) => searchParams.get(f));
    return field ? { field, value: searchParams.get(field) } : null;
  });
  const isActive = (field, value) => cardFilter?.field === field && cardFilter?.value === value;
  const toggleFilter = (field, value) => setCardFilter(isActive(field, value) ? null : { field, value });
  const visibleIncidents = cardFilter ? incidents.filter((i) => i[cardFilter.field] === cardFilter.value) : incidents;
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [formData, setFormData] = useState({
    incident_id: '',
    title: '',
    description: '',
    severity: 'Medium',
    category: 'Unauthorized Access',
    status: 'Reported',
    date_occurred: '',
    reported_by: '',
    assigned_to: '',
    root_cause: '',
    corrective_preventive_action: '',
    lessons_learned: ''
  });

  useEffect(() => {
    fetchIncidents();
  }, [refreshKey, viewingOrg]);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/incidents${vtq()}`);
      setIncidents(response.data);
    } catch (error) {
      toast.error('Failed to fetch incidents');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingIncident) {
        await axios.patch(`${API}/incidents/${editingIncident.id}${vtq()}`, formData);
        toast.success('Incident updated successfully');
      } else {
        await axios.post(`${API}/incidents${vtq()}`, formData);
        toast.success('Incident created successfully');
      }
      setShowDialog(false);
      resetForm();
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (incidentId) => {
    if (window.confirm('Are you sure you want to delete this incident?')) {
      try {
        await axios.delete(`${API}/incidents/${incidentId}${vtq()}`);
        toast.success('Incident deleted successfully');
        refreshData();
      } catch (error) {
        toast.error('Failed to delete incident');
      }
    }
  };

  const handleEdit = (incident) => {
    setEditingIncident(incident);
    setFormData({
      incident_id: incident.incident_id,
      title: incident.title,
      description: incident.description,
      severity: incident.severity,
      category: incident.category,
      status: incident.status,
      date_occurred: incident.date_occurred?.split('T')[0] || '',
      reported_by: incident.reported_by,
      assigned_to: incident.assigned_to || '',
      root_cause: incident.root_cause || '',
      corrective_preventive_action:
        incident.corrective_preventive_action ?? incident.resolution ?? '',
      lessons_learned: incident.lessons_learned || ''
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingIncident(null);
    setFormData({
      incident_id: '',
      title: '',
      description: '',
      severity: 'Medium',
      category: 'Unauthorized Access',
      status: 'Reported',
      date_occurred: '',
      reported_by: '',
      assigned_to: '',
      root_cause: '',
      corrective_preventive_action: '',
      lessons_learned: ''
    });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      Low: 'bg-slate-100 text-green-800',
      Medium: 'bg-slate-100 text-slate-800',
      High: 'bg-slate-100 text-slate-800',
      Critical: 'bg-slate-100 text-red-800'
    };
    return colors[severity] || colors.Medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      Reported: 'bg-slate-100 text-red-800',
      'Under Investigation': 'bg-slate-100 text-slate-800',
      Contained: 'bg-slate-100 text-slate-800',
      Resolved: 'bg-slate-100 text-green-800',
      Closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.Reported;
  };

  // Calculate statistics
  const stats = {
    total: incidents.length,
    bySeverity: {
      low: incidents.filter(i => i.severity === 'Low').length,
      medium: incidents.filter(i => i.severity === 'Medium').length,
      high: incidents.filter(i => i.severity === 'High').length,
      critical: incidents.filter(i => i.severity === 'Critical').length
    },
    byStatus: {
      reported: incidents.filter(i => i.status === 'Reported').length,
      investigating: incidents.filter(i => i.status === 'Under Investigation').length,
      contained: incidents.filter(i => i.status === 'Contained').length,
      resolved: incidents.filter(i => i.status === 'Resolved').length,
      closed: incidents.filter(i => i.status === 'Closed').length
    },
    byCategory: {}
  };

  // Group by category
  incidents.forEach(incident => {
    stats.byCategory[incident.category] = (stats.byCategory[incident.category] || 0) + 1;
  });

  const severityData = [
    { name: 'Low', value: stats.bySeverity.low, color: '#10B981' },
    { name: 'Medium', value: stats.bySeverity.medium, color: '#F59E0B' },
    { name: 'High', value: stats.bySeverity.high, color: '#F97316' },
    { name: 'Critical', value: stats.bySeverity.critical, color: '#EF4444' }
  ];

  const categoryData = Object.entries(stats.byCategory).map(([name, value]) => ({
    name,
    value
  }));

  const exportToCSV = async () => {
    if (incidents.length === 0) {
      toast.error('No incidents to export');
      return;
    }
    try {
      const branding = await fetchTenantBranding(viewingOrg?.id);
      const brandTitle = branding?.name ? `${branding.name} — Incident Register` : 'Incident Register';
      const stamp = new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
      const headers = ['Incident ID', 'Title', 'Description', 'Severity', 'Category', 'Status', 'Date Occurred', 'Reported By', 'Assigned To', 'Root Cause', 'Corrective/Preventive Action', 'Lessons Learned'];
      const rows = incidents.map(i => [
        i.incident_id,
        i.title,
        i.description,
        i.severity,
        i.category,
        i.status,
        i.date_occurred ? new Date(i.date_occurred).toLocaleDateString() : '',
        i.reported_by,
        i.assigned_to || '',
        i.root_cause || '',
        i.corrective_preventive_action ?? i.resolution ?? '',
        i.lessons_learned || ''
      ]);
      const csvContent = [
        `"${brandTitle.replace(/"/g, '""')}"`,
        `"Generated at ${stamp}"`,
        '',
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `Incident_Register_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Incident register exported successfully');
    } catch (e) {
      toast.error('Failed to export incidents');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="incident-register-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight text-slate-900">
            Incident Register
          </h1>
          <p className="text-gray-700 mt-2">{incidents.length} incidents tracked</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/incidents-template`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'incident_register_template.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Template downloaded');
              } catch {
                toast.error('Failed to download template');
              }
            }}
            variant="outline"
            className="rounded-sm"
            data-testid="download-incident-template-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Template
          </Button>
          <Button
            onClick={() => { setImportFile(null); setImportResult(null); setShowImportDialog(true); }}
            variant="outline"
            className="rounded-sm"
            data-testid="import-incidents-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Excel
          </Button>
          <Button
            onClick={exportToCSV}
            variant="outline"
            className="rounded-sm"
            data-testid="export-incidents-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-incident-button" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Report Incident
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-slate-900">
            <DialogHeader>
              <DialogTitle className="text-slate-900">{editingIncident ? 'Edit Incident' : 'Report New Incident'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-900">Incident ID</Label>
                  <Input
                    className="!bg-white !text-slate-900"
                    value={formData.incident_id}
                    onChange={(e) => setFormData({ ...formData, incident_id: e.target.value })}
                    placeholder="INC-001"
                    required
                    disabled={!!editingIncident}
                  />
                </div>
                <div>
                  <Label className="text-slate-900">Date Occurred</Label>
                  <Input
                    className="!bg-white !text-slate-900"
                    type="date"
                    value={formData.date_occurred}
                    onChange={(e) => setFormData({ ...formData, date_occurred: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-900">Title</Label>
                <Input
                  className="!bg-white !text-slate-900"
                    value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Incident title"
                  required
                />
              </div>

              <div>
                <Label className="text-slate-900">Description</Label>
                <Textarea
                  className="!bg-white !text-slate-900"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of the incident"
                  rows={3}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-900">Severity</Label>
                  <select
                    value={formData.severity}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                    className="w-full rounded-sm border border-slate-700 bg-white text-slate-900 px-3 py-2 text-sm"
                  >
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                    <option>Critical</option>
                  </select>
                </div>
                <div>
                  <Label className="text-slate-900">Category</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full rounded-sm border border-slate-700 bg-white text-slate-900 px-3 py-2 text-sm"
                  >
                    <option>Data Breach</option>
                    <option>Malware</option>
                    <option>Phishing</option>
                    <option>Unauthorized Access</option>
                    <option>DDoS</option>
                    <option>Social Engineering</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-900">Reported By</Label>
                  <Input
                    className="!bg-white !text-slate-900"
                    value={formData.reported_by}
                    onChange={(e) => setFormData({ ...formData, reported_by: e.target.value })}
                    placeholder="Reporter name"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-900">Assigned To</Label>
                  <Input
                    className="!bg-white !text-slate-900"
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    placeholder="Assignee name"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-900">Status</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full rounded-sm border border-slate-700 bg-white text-slate-900 px-3 py-2 text-sm"
                >
                  <option>Reported</option>
                  <option>Under Investigation</option>
                  <option>Contained</option>
                  <option>Resolved</option>
                  <option>Closed</option>
                </select>
              </div>

              <div>
                <Label className="text-slate-900">Root Cause</Label>
                <Textarea
                  data-testid="incident-root-cause"
                  className="!bg-white !text-slate-900"
                  value={formData.root_cause}
                  onChange={(e) => setFormData({ ...formData, root_cause: e.target.value })}
                  placeholder="What underlying factor caused this incident?"
                  rows={2}
                />
              </div>

              <div>
                <Label className="text-slate-900">Corrective/Preventive Action</Label>
                <Textarea
                  data-testid="incident-cpa"
                  className="!bg-white !text-slate-900"
                  value={formData.corrective_preventive_action}
                  onChange={(e) => setFormData({ ...formData, corrective_preventive_action: e.target.value })}
                  placeholder="Immediate corrective action taken and preventive controls applied."
                  rows={3}
                />
              </div>
              <div>
                <Label className="text-slate-900">Lessons Learned</Label>
                <Textarea
                  data-testid="incident-lessons"
                  className="!bg-white !text-slate-900"
                  value={formData.lessons_learned}
                  onChange={(e) => setFormData({ ...formData, lessons_learned: e.target.value })}
                  placeholder="Key takeaways and preventive measures"
                  rows={3}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800">
                  {editingIncident ? 'Update' : 'Report'} Incident
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Incident Dashboard */}
      <div className="space-y-3" data-testid="incident-dashboard">
        <div>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Incident Severity</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <IncidentStatCard label="Total" value={stats.total} testid="incident-total" active={!cardFilter} onClick={() => setCardFilter(null)} />
            {SEVERITY_CARDS.map((c) => (
              <IncidentStatCard key={c.label} label={c.label} value={stats.bySeverity[c.key]} tone={c.tone} testid={`incident-severity-${c.key}`}
                active={isActive('severity', c.label)} onClick={() => toggleFilter('severity', c.label)} />
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Incident Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {STATUS_CARDS.map((c) => (
              <IncidentStatCard key={c.label} label={c.label} value={stats.byStatus[c.key]} tone={c.tone} testid={`incident-status-${c.key}`}
                active={isActive('status', c.label)} onClick={() => toggleFilter('status', c.label)} />
            ))}
          </div>
        </div>
      </div>

      {/* Charts */}
      {incidents.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Severity Distribution */}
          <div className="stat-card bg-white">
            <h2 className="text-xl font-heading font-semibold mb-6 text-slate-900">Severity Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={severityData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => value > 0 ? `${name}: ${value}` : null}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {severityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Category Breakdown */}
          <div className="stat-card bg-white">
            <h2 className="text-xl font-heading font-semibold mb-6 text-slate-900">Incidents by Category</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#64748B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {cardFilter && (
        <ActiveFilterChip
          label={`${cardFilter.field === 'severity' ? 'Severity' : 'Status'}: ${cardFilter.value} (${visibleIncidents.length})`}
          onClear={() => setCardFilter(null)}
          testid="incident-active-filter"
        />
      )}

      <div className="border border-border rounded-sm bg-white overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-border">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase w-24">ID</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase">Title & Description</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase w-32">Category</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase w-24">Severity</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase w-32">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase w-24">Date</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visibleIncidents.map((incident, index) => (
                <tr key={incident.id} className="hover:bg-slate-50" data-testid={`incident-row-${index}`}>
                  <td className="px-3 py-3">
                    <span className="text-xs font-mono font-medium text-slate-900">{incident.incident_id}</span>
                  </td>
                  <td className="px-3 py-3">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{incident.title}</div>
                      <div className="text-xs text-slate-700 mt-1 line-clamp-2">{incident.description}</div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-slate-600">{incident.category}</span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${getSeverityColor(incident.severity)}`}>
                      <AlertOctagon className="w-3 h-3 mr-1" />
                      {incident.severity}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${getStatusColor(incident.status)}`}>
                      {incident.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="text-xs text-slate-600">
                      {new Date(incident.date_occurred).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(incident)}
                        data-testid={`edit-incident-${index}`}
                        className="px-2 py-1"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(incident.id)}
                        data-testid={`delete-incident-${index}`}
                        className="px-2 py-1"
                      >
                        <Trash2 className="w-3 h-3 text-red-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Incident Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {visibleIncidents.map((incident, index) => (
          <div
            key={incident.id}
            data-testid={`incident-card-${index}`}
            className="border border-border rounded-sm bg-white p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm font-mono font-medium text-slate-900">{incident.incident_id}</span>
              <span className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ${getSeverityColor(incident.severity)}`}>
                <AlertOctagon className="w-3 h-3 mr-1" />
                {incident.severity}
              </span>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-slate-900 mb-1">{incident.title}</h3>
              <p className="text-xs text-slate-600 line-clamp-2 mb-2">{incident.description}</p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="text-slate-600">Category: {incident.category}</span>
                <span className="text-slate-600">•</span>
                <span className={`font-medium ${getStatusColor(incident.status)}`}>{incident.status}</span>
                <span className="text-slate-600">•</span>
                <span className="text-slate-600">{new Date(incident.date_occurred).toLocaleDateString()}</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleEdit(incident)}
                className="flex-1"
              >
                <Edit className="w-3 h-3 mr-1" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDelete(incident.id)}
              >
                <Trash2 className="w-3 h-3 text-red-600" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {visibleIncidents.length === 0 && (
        <div className="text-center py-12">
          <AlertOctagon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">{cardFilter ? 'No incidents match this filter' : 'No incidents reported yet'}</p>
        </div>
      )}

      {/* Import Incidents Dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="import-incidents-dialog">
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Import Incidents from Excel</h3>
              <button onClick={() => setShowImportDialog(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-4">
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <p className="font-medium text-slate-900 mb-1">How it works</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>Download the template (above) for the expected columns.</li>
                  <li>Required: <b>Title, Description, Category, Severity, Date Occurred, Reported By</b>.</li>
                  <li>Severity / Status accept dropdown values.</li>
                  <li>Missing Incident IDs are auto-generated (INC-###).</li>
                </ul>
              </div>
              <div>
                <Label htmlFor="inc-file">Excel file (.xlsx)</Label>
                <Input
                  id="inc-file"
                  type="file"
                  accept=".xlsx,.xlsm"
                  data-testid="incident-import-file-input"
                  onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                  className="mt-1"
                />
                {importFile && <p className="text-xs text-slate-600 mt-1">Selected: {importFile.name}</p>}
              </div>
              {importResult && (
                <div className="rounded-sm border border-green-200 bg-green-50 p-3 text-sm">
                  <p className="font-medium text-green-900">
                    Imported {importResult.imported} incident(s){importResult.skipped ? `, skipped ${importResult.skipped}` : ''}
                  </p>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <ul className="mt-2 text-xs text-amber-900 list-disc list-inside space-y-0.5">
                      {importResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowImportDialog(false)}>Close</Button>
                <Button
                  onClick={async () => {
                    if (!importFile) { toast.error('Please select a file'); return; }
                    setImporting(true); setImportResult(null);
                    try {
                      const fd = new FormData();
                      fd.append('file', importFile);
                      const res = await axios.post(`${API}/incidents/import${vtq()}`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      setImportResult(res.data);
                      toast.success(res.data.message || 'Import complete');
                      setRefreshKey((k) => k + 1);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Import failed');
                    } finally {
                      setImporting(false);
                    }
                  }}
                  disabled={!importFile || importing}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="confirm-import-incidents-btn"
                >
                  {importing ? 'Importing…' : 'Import'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
